import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OpsHeading, OpsState, OpsTable, errorText } from './OperationsUI';
import { toast } from '@/components/ui/sonner';
import { RefreshCw } from 'lucide-react';
import api from '@/lib/api';

const STATUS = { OPEN: 'Abierto', IN_PROGRESS: 'En curso', RESOLVED: 'Resuelto' };
const statusVariant = (s) => (s === 'RESOLVED' ? 'secondary' : s === 'IN_PROGRESS' ? 'default' : 'outline');

export const PlatformSupport = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState('');
  const [status, setStatus] = useState('OPEN');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows((await api.get('/platform/support/messages')).data); }
    catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (m) => { setActive(m); setReply(''); setStatus(m.status || 'OPEN'); };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/support/messages/${active.id}/reply`, { reply, status });
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      setActive(data); setReply('');
      toast.success('Mensaje actualizado');
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  const filtered = rows.filter((r) => filter === 'ALL' || (r.status || 'OPEN') === filter);
  const openCount = rows.filter((r) => (r.status || 'OPEN') !== 'RESOLVED').length;

  return (
    <>
      <OpsHeading title="Bandeja de soporte" description="Mensajes enviados desde la página de Contacto. Registra respuestas auditables y su estado; cuando el correo está configurado, cada respuesta se envía al remitente y se registra si se envió y a qué hora.">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="support-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            <SelectItem value="OPEN">Abiertos</SelectItem>
            <SelectItem value="IN_PROGRESS">En curso</SelectItem>
            <SelectItem value="RESOLVED">Resueltos</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" data-testid="support-reload" onClick={load}><RefreshCw /> Actualizar</Button>
      </OpsHeading>
      <Alert className="mb-5" data-testid="support-open-count"><AlertDescription>{openCount} mensajes sin resolver de {rows.length} en total.</AlertDescription></Alert>
      <OpsState loading={loading} error={error} reload={load}>
        <OpsTable
          id="platform-support-table"
          rows={filtered}
          columns={[
            { key: 'created_at', label: 'Fecha', render: (r) => new Date(r.created_at).toLocaleString('es') },
            { key: 'name', label: 'Remitente', render: (r) => <span>{r.name}<br /><span className="text-xs text-muted-foreground">{r.email}</span></span> },
            { key: 'subject', label: 'Asunto' },
            { key: 'status', label: 'Estado', render: (r) => <Badge variant={statusVariant(r.status || 'OPEN')} data-testid={`support-status-${r.id}`}>{STATUS[r.status || 'OPEN']}</Badge> },
            { key: 'replies', label: 'Respuestas', render: (r) => (r.replies || []).length },
          ]}
          actions={(r) => <Button variant="outline" data-testid={`support-open-${r.id}`} onClick={() => open(r)}>Abrir</Button>}
        />
      </OpsState>
      <Dialog open={!!active} onOpenChange={(v) => { if (!v && !busy) setActive(null); }}>
        <DialogContent className="seller-dialog" data-testid="support-dialog">
          <DialogHeader>
            <DialogTitle>{active?.subject}</DialogTitle>
            <DialogDescription>De {active?.name} · {active?.email}{active?.user_id ? ' · cuenta registrada' : ' · sin sesión'}</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-3 text-sm" data-testid="support-message-body">{active.message}</div>
              {(active.replies || []).length > 0 && (
                <div className="space-y-2" data-testid="support-replies">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Respuestas internas</Label>
                  {active.replies.map((rep) => (
                    <div key={rep.id} className="border border-border rounded-lg p-3 text-sm" data-testid={`support-reply-${rep.id}`}>
                      <p>{rep.text}</p>
                      <p className="text-xs text-muted-foreground mt-1">{rep.author} · {new Date(rep.at).toLocaleString('es')}</p>
                      {rep.emailed ? <p className="text-xs text-emerald-600 mt-1" data-testid={`support-reply-emailed-${rep.id}`}>Enviado por correo · {rep.emailed_at ? new Date(rep.emailed_at).toLocaleString('es') : ''}</p> : (rep.text ? <p className="text-xs text-muted-foreground mt-1" data-testid={`support-reply-notemailed-${rep.id}`}>Registrado internamente (sin envío por correo)</p> : null)}
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label htmlFor="support-reply-text" className="text-xs">Respuesta interna (auditable)</Label>
                <Textarea id="support-reply-text" rows={4} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="support-reply-text" className="mt-1" placeholder="Redacta la respuesta o nota de gestión…" />
              </div>
              <div>
                <Label className="text-xs">Estado</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="support-status-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPEN">Abierto</SelectItem>
                    <SelectItem value="IN_PROGRESS">En curso</SelectItem>
                    <SelectItem value="RESOLVED">Resuelto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">Si escribes una respuesta, se enviará por correo al remitente cuando el correo esté configurado; en cualquier caso queda registrada con autor y fecha.</p>
              <Button onClick={submit} disabled={busy} data-testid="support-save" className="seller-primary">{busy ? 'Guardando…' : 'Guardar respuesta y estado'}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PlatformSupport;
